import { Platform } from 'react-native';
import { getIntl } from '../../../../../utils/intl';
import useNotify from '../../../../../hooks/useNotify';

const intl = getIntl('useDownloadQRCode', {
    success: 'QR code downloaded successfully',
    error: 'Failed to download QR code',
});

export type UseDownloadQRCodeReturnValue = {
    enableDownload: boolean;
    onDownloadQRCode: () => void;
};

export type QRCodeHeaderInfo = {
    name: string;
    ageString?: string;
    reportName: string;
};

export const useDownloadQRCode = (
    element: SVGAElement | null,
    headerInfo?: QRCodeHeaderInfo,
): UseDownloadQRCodeReturnValue => {
    const notify = useNotify();
    if (Platform.OS !== 'web' || !element) {
        return {
            enableDownload: false,
            onDownloadQRCode: () => {
                // do nothing
            },
        };
    }

    return {
        enableDownload: true,
        onDownloadQRCode: () => {
            try {
                function triggerDownload(imgURI: string) {
                    const a = document.createElement('a');
                    a.download = 'SharedReportQRCode.png'; // filename
                    a.target = '_blank';
                    a.href = imgURI;

                    // trigger download button
                    // (set `bubbles` to false here.
                    // or just `a.click()` if you don't care about bubbling)
                    a.dispatchEvent(
                        new MouseEvent('click', {
                            view: window,
                            bubbles: false,
                            cancelable: true,
                        }),
                    );
                }

                const svgNode = element;
                const svgString = new XMLSerializer().serializeToString(element);
                const svgBlob = new Blob([svgString], {
                    type: 'image/svg+xml;charset=utf-8',
                });

                const DOMURL = window.URL || window.webkitURL || window;
                const url = DOMURL.createObjectURL(svgBlob);

                const image = new Image();
                image.width = svgNode?.getBBox().width || 0;
                image.height = svgNode?.getBBox().height || 0;
                image.src = url;
                image.onload = function () {
                    const canvas = document.createElement('canvas');

                    // Calculate header height
                    const lineHeight = 40;
                    const headerPadding = 5;
                    let headerLines = headerInfo ? 2 : 0;
                    if (headerInfo?.ageString) headerLines++;
                    const totalHeaderHeight = headerLines * lineHeight + headerPadding;

                    canvas.width = image.width;
                    canvas.height = image.height + totalHeaderHeight;

                    const ctx = canvas.getContext('2d');

                    if (ctx) {
                        // Fill background with white
                        ctx.fillStyle = 'white';
                        ctx.fillRect(0, 0, canvas.width, canvas.height);

                        // Draw header text if provided
                        if (headerInfo) {
                            ctx.fillStyle = 'black';
                            ctx.textAlign = 'left';
                            ctx.textBaseline = 'middle';
                            const leftPadding = 5;

                            // Name (bold, larger)
                            ctx.font = 'bold 32px Arial, sans-serif';
                            ctx.fillText(headerInfo.name, leftPadding, lineHeight / 2);

                            // DOB + Age
                            ctx.font = '24px Arial, sans-serif';
                            if (headerInfo.ageString) {
                                ctx.fillText(headerInfo.ageString, leftPadding, lineHeight + lineHeight / 2);
                            }

                            // Report Name
                            let reportNameY = 2 * lineHeight + lineHeight / 2;
                            if (!headerInfo.ageString) reportNameY -= lineHeight;
                            ctx.fillText(headerInfo.reportName, leftPadding, reportNameY);
                        }

                        // Draw QR code below header
                        ctx.drawImage(image, 0, totalHeaderHeight);
                    }

                    DOMURL.revokeObjectURL(url);

                    const imgURI = canvas.toDataURL('image/png').replace('image/png', 'image/octet-stream');
                    triggerDownload(imgURI);
                };
            } catch (error) {
                console.error(error);
                notify('error', intl('error'));
            }
        },
    };
};
